import { FC, Key, useCallback, useState } from 'react';
import { useTranslation } from '@packages/utils';
import { FilterValue } from 'antd/es/table/interface';
import { Modal } from 'components/Base/Modal';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import ExportDropdown from 'components/Shared/ExportDropdown';
import ConfirmModalBody from 'components/Shared/ConfirmModalBody';
import {
  ETableStoragePaths,
  TColumnsState,
  TColumnType,
} from 'components/Base/Table/Table.types';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useExportParentsCSVMutation } from 'redux/apiSlice/kitManagement.slice';
import { buildParentOrdersExportBody } from 'data/convertors/kitManagement.converter';
import {
  EParentOrderStatus,
  TKitManagementParentUI,
} from 'data/types/kitManagement.types';
import { TRtkErrorType, TSortParams } from 'data/types/generic.types';

type TParentExportProps = {
  selectedRowKeys: Key[];
  pageData: TKitManagementParentUI[];
  selectedFilters?: Record<string, FilterValue | null>;
  sortColumn?: TSortParams;
  columns: TColumnType<TKitManagementParentUI>[];
};

const ParentExport: FC<TParentExportProps> = ({
  selectedRowKeys,
  pageData,
  selectedFilters,
  sortColumn,
  columns,
}) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.kit-management.list.actions.export',
  });
  const [exportParents, { isLoading: isExporting }] =
    useExportParentsCSVMutation();
  const { showToastNotification } = useShowToastNotification();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const getVisibleColumnKeys = useCallback((): string[] => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.KIT_MANAGEMENT_PARENT
    ] as TColumnsState[] | undefined;
    if (columnsState?.length) {
      return columnsState
        .filter(col => col.show && col.key !== 'actions')
        .map(col => String(col.key));
    }
    return columns
      .filter(col => col.key !== 'actions')
      .map(col => String(col.key));
  }, [columns]);

  // Status is only known for rows visible on the current page; cross-page selections
  // have undefined status, so the confirmation prompt is best-effort.
  const orderedRowCount = selectedRowKeys
    .map(key => pageData.find(row => row.parentOrderId === key))
    .filter(row => row?.status === EParentOrderStatus.ORDERED).length;
  const isAllOrdered =
    orderedRowCount > 0 && orderedRowCount === selectedRowKeys.length;

  const runExport = async (resourceIds?: string[]) => {
    try {
      const body = buildParentOrdersExportBody({
        filters: selectedFilters,
        sortColumn,
        visibleColumnKeys: getVisibleColumnKeys(),
        resourceIds,
      });
      await exportParents(body).unwrap();
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message || 'Export failed.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleExportSelected = () => {
    if (!selectedRowKeys.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    if (orderedRowCount > 0) {
      setConfirmOpen(true);
      return;
    }
    runExport(selectedRowKeys.map(key => String(key)));
  };

  const handleConfirmSelected = async () => {
    await runExport(selectedRowKeys.map(key => String(key)));
    setConfirmOpen(false);
  };

  const handleExportAll = () => {
    runExport();
  };

  return (
    <>
      <ExportDropdown
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
      <Modal
        open={confirmOpen}
        title={t('modal.TITLE')}
        okText={t('modal.SUBMIT_BTN')}
        cancelText={t('modal.CANCEL_BTN')}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleConfirmSelected}
        confirmLoading={isExporting}
      >
        <ConfirmModalBody
          description={t('modal.DESCRIPTION')}
          title={
            isAllOrdered
              ? t('modal.subtitle.IS_ALL_ORDERED')
              : t('modal.subtitle.IS_SOME_ORDERED', {
                  placeholder: orderedRowCount,
                })
          }
        />
      </Modal>
    </>
  );
};

export default ParentExport;
